'use client'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { createClient } from '~/lib/supabase/client'
import { env } from '~/env'
import SwipeDeck, { type DeckEntry } from '~/components/match/SwipeDeck'

/**
 * The matching game — a swipe session over a deterministically scored deck.
 * Launched from the /match page CTA; the carousels there stay untouched.
 */
export default function MatchGamePage() {
  const supabase = createClient()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [session, setSession] = useState<{ id: string; deck: DeckEntry[] } | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'unauthenticated' | 'error'>('loading')

  const startSession = useCallback(async (token: string) => {
    setState('loading')
    try {
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/engagement/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ source: 'match_page' }),
      })
      if (!res.ok) throw new Error(`Session create failed: ${res.status}`)
      const data = (await res.json()) as { id: string; deck: DeckEntry[] }
      setSession(data)
      setState('ready')
    } catch (error) {
      console.error('Failed to start matching game session:', error)
      setState('error')
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      if (!token) {
        setState('unauthenticated')
        return
      }
      setAccessToken(token)
      await startSession(token)
    }
    void init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="fixed inset-0 top-16 overflow-auto bg-[#F4ECE1]">
      <main className="min-h-full px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-2xl space-y-6">
          <div className="flex items-center justify-between">
            <Link
              href="/match"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#5E594F] hover:text-[#2C2D2B]"
            >
              <ArrowLeft className="h-4 w-4" /> Back to My Matches
            </Link>
            <h1 className="text-lg font-bold text-[#2C2D2B]">Matching Game</h1>
          </div>

          {state === 'loading' && (
            <div className="flex h-[520px] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#DE866D] border-t-transparent" />
            </div>
          )}

          {state === 'unauthenticated' && (
            <div className="rounded-[22px] border border-[#EFE7D7] bg-[#FFFDF8] p-8 text-center">
              <p className="text-sm text-[#5E594F]">Sign in to play the matching game.</p>
              <Link href="/login" className="mt-3 inline-block text-sm font-semibold text-[#DE866D] hover:underline">
                Go to login
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div className="rounded-[22px] border border-[#EFE7D7] bg-[#FFFDF8] p-8 text-center">
              <p className="text-sm text-[#5E594F]">
                Something went wrong starting your game. Please try again.
              </p>
              <button
                type="button"
                onClick={() => accessToken && startSession(accessToken)}
                className="mt-3 rounded-full bg-[#DE866D] px-5 py-2 text-sm font-semibold text-white hover:bg-[#c9755e]"
              >
                Retry
              </button>
            </div>
          )}

          {state === 'ready' && session && accessToken && (
            <SwipeDeck
              key={session.id}
              sessionId={session.id}
              deck={session.deck}
              accessToken={accessToken}
              onRestart={() => startSession(accessToken)}
            />
          )}
        </div>
      </main>
    </div>
  )
}
